import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Drawer, Typography } from '@mui/material';
import AutocompleteCDS from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { showErrorMessageAction } from 'store/global/actions';
import { saveCallAction, TELEPHONY_ACTION_TYPE } from 'store/system/actions';
import { selectCallState } from 'store/system/selectors';

import {
  DISPOSITION_FIELD_NAME,
  DISPOSITION_OPTIONS,
  NOTE_FIELD_NAME,
} from '../../MobileCallSurface.constants';
import { getCallerDisplayNumber } from '../../MobileCallSurface.utils';

import {
  buttonRowStyles,
  drawerPaperStyles,
  formStyles,
  grabberStyles,
  headerStyles,
  saveButtonStyles,
  skipButtonStyles,
  subtitleStyles,
} from './PostCallSheet.styles';
import { PostCallSheetTestIds } from './PostCallSheet.testIds';

const drawerPaperProps = { sx: drawerPaperStyles };

const PostCallSheet = ({ isOpen }) => {
  const dispatch = useDispatch();
  const callState = useSelector(selectCallState);
  const [disposition, setDisposition] = useState(null);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [dispositionError, setDispositionError] = useState('');

  const onDispositionChange = useCallback((_event, value) => {
    setDisposition(value);
    setDispositionError('');
  }, []);

  const onNoteChange = useCallback(event => {
    setNote(event.target.value);
  }, []);

  const onSkip = useCallback(() => {
    dispatch({ type: TELEPHONY_ACTION_TYPE.CALL_RESET });
  }, [dispatch]);

  const onSave = useCallback(async () => {
    if (_.isNil(disposition)) {
      setDispositionError('Please select an outcome');

      return;
    }

    setIsSaving(true);

    try {
      await dispatch(
        saveCallAction({
          dispositionType: disposition.value,
          note,
          telnyxCallId: callState.callId,
        }),
      );
    } catch (error) {
      dispatch(showErrorMessageAction({ message: 'Failed to save call summary', err: error }));
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, disposition, note, callState.callId]);

  const displayNumber = getCallerDisplayNumber(callState) || 'Unknown';

  return (
    <Drawer
      PaperProps={drawerPaperProps}
      anchor="bottom"
      data-testid={PostCallSheetTestIds.DRAWER}
      open={isOpen}
      onClose={onSkip}
    >
      <Box sx={grabberStyles} />

      <Box sx={headerStyles}>
        <Typography variant="bold20">Call summary</Typography>
        <Typography sx={subtitleStyles} variant="regular14">
          {displayNumber}
        </Typography>
      </Box>

      <Box sx={formStyles}>
        <AutocompleteCDS
          disablePortal
          dataTestId={PostCallSheetTestIds.DISPOSITION}
          error={!!dispositionError}
          helperText={dispositionError}
          label="Outcome"
          name={DISPOSITION_FIELD_NAME}
          optionName="name"
          options={DISPOSITION_OPTIONS}
          placeholder="Select outcome"
          value={disposition}
          onChange={onDispositionChange}
        />

        <TextFieldCDS
          multiline
          dataTestId={PostCallSheetTestIds.NOTE}
          label="Notes"
          name={NOTE_FIELD_NAME}
          placeholder="Add notes about this call…"
          rows={3}
          value={note}
          onChange={onNoteChange}
        />
      </Box>

      <Box sx={buttonRowStyles}>
        <Button
          data-testid={PostCallSheetTestIds.SKIP}
          disabled={isSaving}
          sx={skipButtonStyles}
          variant="outlined"
          onClick={onSkip}
        >
          Skip
        </Button>
        <Button
          data-testid={PostCallSheetTestIds.SAVE}
          disabled={_.isNil(disposition) || isSaving}
          sx={saveButtonStyles}
          variant="contained"
          onClick={onSave}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Drawer>
  );
};

PostCallSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default memo(PostCallSheet);
